import React, { useMemo, useState } from "react";
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stack, Alert, CircularProgress
} from "@mui/material";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../AuthContext";

// Hack: restrict this action to a single user for now
const ALLOWED_EMAIL = "haavard@everviz.com";

export default function ActivateTrialDialog({ teamId, teamName, onActivated }) {
  const { userEmail } = useAuth();
  const [open, setOpen] = useState(false);
  const [trialDays, setTrialDays] = useState(14);
  const [company, setCompany] = useState(""); 
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const setDays = (v) => {
    const n = parseInt(v, 10);
    if (!Number.isFinite(n)) return setTrialDays(1);
    setTrialDays(Math.min(Math.max(n, 1), 20));
  };

  // Keep disabled minimal: just what's required
  const disabled = useMemo(() => {
    return saving || !teamId || trialDays < 1;
  }, [saving, teamId, trialDays]);

  const handleOpen = () => { setError(""); setOpen(true); };
  const handleClose = () => { if (!saving) setOpen(false); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const body = {
        trialDays: Number(trialDays),
        company: company.trim() || undefined,
      };
      const res = await axiosInstance.post(`/api/team/activate-trial/${teamId}`, body);
      onActivated?.(res.data);
      setOpen(false);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || "Failed to activate trial.");
    } finally {
      setSaving(false);
    }
  };

  if (userEmail !== ALLOWED_EMAIL) return null;

  return (
    <>
      <Button variant="contained" onClick={handleOpen}>
        Activate Enterprise trial
      </Button>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={submit}>
          <DialogTitle>Activate full blown trial for {teamName}</DialogTitle>
          <DialogContent dividers>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Stack spacing={2}>
              <TextField
                label="Trial length (days)"
                type="number"
                value={trialDays}
                onChange={(e) => setDays(e.target.value)}
                inputProps={{ min: 1, max: 20 }}
                required
              />

              <TextField
                fullWidth
                label="Company"
                placeholder="Acme Co"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                required
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={saving}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={disabled}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? "Activating…" : "Activate trial"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
